import { readGoogleCallbackUrl } from '@/modules/auth/googleCallbackUrl';
import {
  clearGoogleSigninState,
  completeGoogleLoginFromCallback,
  GoogleSsoError,
  readGoogleSignInRequest,
  startGoogleLogin,
} from '@/modules/auth/googleSso';
import { authProviders } from '@/modules/auth/types/externalLogin';

import { OAuthCallbackStatus } from './oauthCallbackStatus/OAuthCallbackStatus';
import {
  type OAuthCallbackAdapter,
  useOAuthCallback,
} from './oauthCallbackStatus/useOAuthCallback';

// `PostHogProvider` strips the ID-token fragment from the address bar before
// this runs, so the full callback URL must come from `readGoogleCallbackUrl`.
const googleCallback: OAuthCallbackAdapter = {
  provider: 'Google',
  ssoEvent: 'googleSSO',
  ssoFailureReason: 'google_sso_error',
  complete: async (callbackUrl) => {
    const { idToken, ...request } = completeGoogleLoginFromCallback(
      readGoogleCallbackUrl(callbackUrl),
    );
    return {
      request,
      credentials: { provider: authProviders.google, idToken },
    };
  },
  readRequest: async (callbackUrl) =>
    readGoogleSignInRequest(readGoogleCallbackUrl(callbackUrl)),
  clearState: clearGoogleSigninState,
  start: ({ returnTo, acceptTerms, intent }) =>
    startGoogleLogin(returnTo, acceptTerms, intent),
  getSsoErrorCode: (error) =>
    error instanceof GoogleSsoError ? error.code : undefined,
};

/**
 * `/auth/callback/google`: Google redirects here with an ID token in the URL
 * fragment (see `googleSso.ts`), which the API's external login verifies.
 */
export const GoogleSignInCallback = () => (
  <OAuthCallbackStatus
    provider="Google"
    {...useOAuthCallback(googleCallback)}
  />
);
